import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import cartService from 'services/cartService';
import { Cart, CartItem } from 'types/cart';
import { ProductSummary } from 'types/product';

interface CartContextValue {
  cart: Cart | null;
  itemCount: number;
  loading: boolean;
  refreshCart: () => Promise<void>;
  addProduct: (product: ProductSummary, quantity?: number) => Promise<void>;
  updateQuantity: (productId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
}

const emptyCart: Cart = { items: [], subtotal: 0, discount: 0, total: 0, currency: 'ARS' };

const CartContext = createContext<CartContextValue | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(false);

  const refreshCart = useCallback(async () => {
    setLoading(true);
    try {
      const data = await cartService.getCart();
      setCart(data);
    } catch {
      setCart(emptyCart);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const addProduct = async (product: ProductSummary, quantity = 1) => {
    const sku = product.sku || product.id;
    const data = await cartService.addItem({ productId: sku, quantity });
    setCart(data);
  };

  const updateQuantity = async (productId: string, quantity: number) => {
    const lineId = cart?.items.find((i) => i.productId === productId)?.lineId;
    const data = await cartService.updateItem({ productId, quantity, lineId });
    setCart(data);
  };

  const clearCart = async () => {
    await cartService.clearCart();
    setCart(emptyCart);
  };

  const itemCount = useMemo(
    () => cart?.items.reduce((sum: number, item: CartItem) => sum + item.quantity, 0) ?? 0,
    [cart]
  );

  const value = useMemo(
    () => ({ cart, itemCount, loading, refreshCart, addProduct, updateQuantity, clearCart }),
    [cart, itemCount, loading, refreshCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCartContext = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCartContext debe usarse dentro de CartProvider');
  return ctx;
};
